import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { CRICKET_API_URL } from "../config";
import { UserContext } from "../context/UserContext";
import {
  getPlayerId,
  normalizeTeamSelection,
  resolvePlayerSelectionId,
} from "../utils/playerIdentity";
import {
  getPlayerLineupIndicator,
  isLineupOutForMatch,
  isPlayerEligibleForLeadership,
} from "../utils/playerLineupStatus";

const parseMatchTime = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    const normalizedValue = value < 10000000000 ? value * 1000 : value;
    const parsed = new Date(normalizedValue);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === "string") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;

    const fallback = new Date(value.replace(" ", "T"));
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  return null;
};

const getStoredTeamDraft = (matchId) => {
  try {
    const matchDraft =
      matchId ? localStorage.getItem(`draft_team_${matchId}`) : null;
    if (matchDraft) {
      return JSON.parse(matchDraft);
    }

    const genericTeam = localStorage.getItem("team");
    return genericTeam ? JSON.parse(genericTeam) : null;
  } catch (error) {
    return null;
  }
};

const normalizeCaptainStat = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const parsedValue = Number.parseFloat(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const formatCaptainStat = (value) => {
  const normalizedValue = normalizeCaptainStat(value) || 0;
  return Number.isInteger(normalizedValue)
    ? normalizedValue
    : Number(normalizedValue.toFixed(1));
};

const getChooseCaptainPlayerKey = (player = {}) =>
  getPlayerId(player) ||
  `${String(player?.name || "").trim().toLowerCase()}::${String(
    player?.team || "",
  )
    .trim()
    .toUpperCase()}`;

const getChooseCaptainPoints = (player = {}) =>
  normalizeCaptainStat(
    player?.totalPoints ?? player?.totalSeriesPoints ?? player?.points,
  ) ||
  normalizeCaptainStat(player?.points) ||
  0;

const getChooseCaptainPercentage = (player = {}) =>
  normalizeCaptainStat(player?.captainPercentage) ?? 0;

const getChooseViceCaptainPercentage = (player = {}) =>
  normalizeCaptainStat(player?.viceCaptainPercentage) ?? 0;

const normalizeCaptainSelectionPlayer = (player = {}) => ({
  ...player,
  totalPoints: getChooseCaptainPoints(player),
  captainPercentage: getChooseCaptainPercentage(player),
  viceCaptainPercentage: getChooseViceCaptainPercentage(player),
});

function ChooseCaptain() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(UserContext);

  const navigationState = useMemo(() => location.state || {}, [location.state]);
  const { matchData, contestToJoin } = navigationState;
  const matchId = matchData?.matchId || matchData?._id || matchData?.id;
  const draftTeam = useMemo(() => getStoredTeamDraft(matchId), [matchId]);
  const selectedTeam = useMemo(() => {
    const selectedPlayersFromState = Array.isArray(navigationState.selectedPlayers)
      ? navigationState.selectedPlayers
      : [];
    const sourceTeam =
      (navigationState.team && typeof navigationState.team === "object"
        ? {
            ...navigationState.team,
            players:
              Array.isArray(navigationState.team?.players) &&
              navigationState.team.players.length > 0
                ? navigationState.team.players
                : selectedPlayersFromState,
          }
        : null) ||
      (selectedPlayersFromState.length > 0
        ? {
            players: selectedPlayersFromState,
            captainId: navigationState.captainId,
            viceCaptainId:
              navigationState.viceCaptainId || navigationState.vcId,
          }
        : null) ||
      draftTeam ||
      null;
    if (!sourceTeam || typeof sourceTeam !== "object") return null;

    const normalizedSourceTeam = normalizeTeamSelection(sourceTeam);
    const sourcePlayersByKey = new Map(
      (sourceTeam.players || []).map((player) => [
        getChooseCaptainPlayerKey(player),
        player,
      ]),
    );
    const normalizedPlayers = Array.from(
      new Map(
        (normalizedSourceTeam.players || []).map((player) => [
          getChooseCaptainPlayerKey(player),
          normalizeCaptainSelectionPlayer({
            ...(sourcePlayersByKey.get(getChooseCaptainPlayerKey(player)) || {}),
            ...player,
          }),
        ]),
      ).values(),
    ).slice(0, 11);

    return {
      ...normalizedSourceTeam,
      players: normalizedPlayers,
      captainId: resolvePlayerSelectionId({
        players: normalizedPlayers,
        playerId: normalizedSourceTeam.captainId,
        player: normalizedSourceTeam.captain,
        playerIndex: normalizedSourceTeam.captainIndex,
      }),
      viceCaptainId: resolvePlayerSelectionId({
        players: normalizedPlayers,
        playerId: normalizedSourceTeam.viceCaptainId,
        player: normalizedSourceTeam.viceCaptain,
        playerIndex: normalizedSourceTeam.viceCaptainIndex,
      }),
    };
  }, [
    draftTeam,
    navigationState.captainId,
    navigationState.selectedPlayers,
    navigationState.team,
    navigationState.vcId,
    navigationState.viceCaptainId,
  ]);
  const playersList = useMemo(() => selectedTeam?.players || [], [selectedTeam]);
  const shouldShowPlayingStatus = isLineupOutForMatch(matchData);

  // 🔥 CACHING LOGIC FOR C/VC DRAFT 🔥
  const cvcDraftKey = `draft_cvc_${matchId}`;

  const getCachedCVC = () => {
    try {
      const cached = sessionStorage.getItem(cvcDraftKey);
      if (!cached) {
        return { captainId: null, viceCaptainId: null };
      }

      const parsed = JSON.parse(cached);
      const cachedPlayers = selectedTeam?.players || parsed?.players || [];

      return {
        captainId: resolvePlayerSelectionId({
          players: cachedPlayers,
          playerId: parsed?.captainId,
          player: parsed?.captain,
          playerIndex: parsed?.captainIndex,
        }),
        viceCaptainId: resolvePlayerSelectionId({
          players: cachedPlayers,
          playerId: parsed?.viceCaptainId || parsed?.vcId,
          player: parsed?.viceCaptain,
          playerIndex: parsed?.viceCaptainIndex,
        }),
      };
    } catch (e) { return { captainId: null, viceCaptainId: null }; }
  };
  const cachedCVC = getCachedCVC();
  const initialCaptainId =
    resolvePlayerSelectionId({
      players: playersList,
      playerId: navigationState.captainId || selectedTeam?.captainId,
      player: navigationState.captain || selectedTeam?.captain,
      playerIndex: navigationState.captainIndex ?? selectedTeam?.captainIndex,
    }) ||
    cachedCVC.captainId;
  const initialViceCaptainId =
    resolvePlayerSelectionId({
      players: playersList,
      playerId:
        navigationState.viceCaptainId ||
        navigationState.vcId ||
        selectedTeam?.viceCaptainId ||
        selectedTeam?.vcId,
      player: navigationState.viceCaptain || selectedTeam?.viceCaptain,
      playerIndex:
        navigationState.viceCaptainIndex ?? selectedTeam?.viceCaptainIndex,
    }) || cachedCVC.viceCaptainId;

  // State Initialize with Cached Values
  const [captainId, setCaptainId] = useState(initialCaptainId);
  const [viceCaptainId, setViceCaptainId] = useState(initialViceCaptainId);

  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [sortBy, setSortBy] = useState("type");
  const [timeLeft, setTimeLeft] = useState("");
  const [selectionWarning, setSelectionWarning] = useState("");
  // 🔥 NEW STATE FOR SUCCESS MODAL
  const [showSuccessModal, setShowSuccessModal] = useState({ show: false, message: "" });

  const captain = useMemo(
    () => playersList.find((player) => getPlayerId(player) === captainId) || null,
    [playersList, captainId]
  );
  const viceCaptain = useMemo(
    () => playersList.find((player) => getPlayerId(player) === viceCaptainId) || null,
    [playersList, viceCaptainId]
  );
  const isPlayerAvailableForLeadership = useCallback(
    (player) => isPlayerEligibleForLeadership(player, shouldShowPlayingStatus),
    [shouldShowPlayingStatus],
  );
  const showSelectionWarningToast = useCallback(
    (message) => setSelectionWarning(message),
    [],
  );

  const getPlayerStatusItems = (player) =>
    shouldShowPlayingStatus
      ? [getPlayerLineupIndicator(player)].filter(Boolean)
      : [];

  // --- SAVE C/VC TO DRAFT WHENEVER THEY CHANGE ---
  useEffect(() => {
    if (captainId || viceCaptainId) {
      sessionStorage.setItem(cvcDraftKey, JSON.stringify({ captainId, viceCaptainId }));
    } else {
      sessionStorage.removeItem(cvcDraftKey);
    }
  }, [captainId, viceCaptainId, cvcDraftKey]);

  useEffect(() => {
    if (!selectionWarning) return undefined;

    const timeout = setTimeout(() => setSelectionWarning(""), 2500);
    return () => clearTimeout(timeout);
  }, [selectionWarning]);

  useEffect(() => {
    if (!playersList || playersList.length !== 11) {
      navigate("/create-team", { state: { matchData, contestToJoin } });
    } else {
      setInitializing(false);
    }
  }, [playersList, navigate, matchData, contestToJoin]);

  useEffect(() => {
    if (!playersList.length) return;

    const latestCaptain = captainId
      ? playersList.find((player) => getPlayerId(player) === captainId) || null
      : null;
    const latestViceCaptain = viceCaptainId
      ? playersList.find((player) => getPlayerId(player) === viceCaptainId) || null
      : null;
    let warningMessage = "";

    if (captainId && (!latestCaptain || !isPlayerAvailableForLeadership(latestCaptain))) {
      setCaptainId(null);
      warningMessage = "Not playing players can't be selected as Captain.";
    }

    if (viceCaptainId && (!latestViceCaptain || !isPlayerAvailableForLeadership(latestViceCaptain))) {
      setViceCaptainId(null);
      warningMessage = warningMessage || "Not playing players can't be selected as Vice Captain.";
    }

    if (
      latestCaptain &&
      latestViceCaptain &&
      getPlayerId(latestCaptain) === getPlayerId(latestViceCaptain)
    ) {
      setViceCaptainId(null);
      warningMessage = warningMessage || "Captain and Vice Captain must be different players.";
    }

    if (warningMessage) {
      showSelectionWarningToast(warningMessage);
    }
  }, [
    playersList,
    captainId,
    viceCaptainId,
    isPlayerAvailableForLeadership,
    showSelectionWarningToast,
  ]);

// --- TIMER LOGIC (ChooseCaptain.jsx) ---
    useEffect(() => {
        const matchStartTime = matchData?.matchStartTime || matchData?.startTime || matchData?.date;
        if (!matchStartTime) {
            setTimeLeft("Time TBA");
            return;
        }

        const updateTimer = () => {
            try {
                const parsedMatchTime = parseMatchTime(matchStartTime);
                const start = parsedMatchTime?.getTime();
                const now = new Date().getTime();
                const distance = start - now;

                if (!start || Number.isNaN(start)) {
                    setTimeLeft("Time TBA");
                    return;
                }

                if (distance <= 0) {
                    setTimeLeft("Live");
                    return;
                }

                const totalHours = Math.floor(distance / (1000 * 60 * 60));
                const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((distance % (1000 * 60)) / 1000);

                setTimeLeft(`${totalHours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`);
            } catch (e) {
                setTimeLeft("Time TBA");
            }
        };

        updateTimer();
        const interval = setInterval(() => {
            updateTimer();
        }, 1000);

        return () => clearInterval(interval);
    }, [matchData]);

  const handleBack = () => {
    navigate("/create-team", {
      state: {
        matchData,
        team: {
          players: playersList,
          captainId,
          viceCaptainId,
        },
        contestToJoin,
        captainId,
        viceCaptainId,
      },
      replace: true
    });
  };

  const toggleCaptain = (player) => {
    const playerId = getPlayerId(player);

    if (!isPlayerAvailableForLeadership(player)) {
      showSelectionWarningToast(`${player.name} is not playing, so Captain selection is disabled.`);
      return;
    }

    if (captainId === playerId) {
      setCaptainId(null);
    } else {
      setCaptainId(playerId);
      if (viceCaptainId === playerId) setViceCaptainId(null);
    }
  };

  const toggleViceCaptain = (player) => {
    const playerId = getPlayerId(player);

    if (!isPlayerAvailableForLeadership(player)) {
      showSelectionWarningToast(`${player.name} is not playing, so Vice Captain selection is disabled.`);
      return;
    }

    if (viceCaptainId === playerId) {
      setViceCaptainId(null);
    } else {
      if (captainId === playerId) return;
      setViceCaptainId(playerId);
    }
  };

  const isComplete = Boolean(captainId && viceCaptainId);



  const handleSaveTeam = async () => {
    if (!isComplete) return alert("Please select Captain and Vice-Captain");
    if (!isPlayerAvailableForLeadership(captain) || !isPlayerAvailableForLeadership(viceCaptain)) {
      showSelectionWarningToast("Captain and Vice Captain must be selected from playing players.");
      return;
    }

    setLoading(true);
    const token = localStorage.getItem("token");

    let currentUserId = user?._id || user?.id;
    if (!currentUserId) {
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const parsedUser = JSON.parse(userStr);
          currentUserId = parsedUser?._id || parsedUser?.id || parsedUser?.userId || parsedUser?.user?._id;
        }
        if (!currentUserId) {
          currentUserId = localStorage.getItem("userId") || localStorage.getItem("_id");
        }
      } catch (e) { }
    }

    if (!currentUserId) {
      alert("Session expired or User ID missing. Please login again.");
      navigate("/login");
      return;
    }

    try {
      // 🔥 FIX 1: Find the actual Team ID passed from CreateTeam -> Contests
      const actualTeamId = navigationState.teamId || selectedTeam?._id || selectedTeam?.id;

      const teamPayload = {
        userId: currentUserId,
        matchId: matchId,
        teamId: actualTeamId,
        captainId,
        viceCaptainId,
        players: playersList.map(p => ({
          id: getPlayerId(p),
          name: p.name,
          team: p.team,
          role: p.role,
          image: p.image,
          points: p.points,
          credits: p.credits
        }))
      };

      console.log("Saving/Updating TEAM ID:", actualTeamId);

      let res;

      // 🔥 FIX 2: Correctly route to PUT or POST based on teamId existence
      if (actualTeamId) {
        // 🔵 EDIT MODE → UPDATE API
        res = await axios.put(
          `${CRICKET_API_URL}/api/cricket/edit-team/${actualTeamId}`,
          teamPayload,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      } else {
        // 🟢 CREATE MODE → SAVE API
        res = await axios.post(
          `${CRICKET_API_URL}/api/cricket/save-team`,
          teamPayload,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }

      if (
        res.data.message === "Team saved successfully!" ||
        res.data.message === "Team updated successfully!"
      ) {
        const newTeam = res.data.team;

        // Team successful save hone ke baad draft clear kar do
        sessionStorage.removeItem(cvcDraftKey);
        localStorage.removeItem(`draft_team_${matchId}`);

        if (contestToJoin) {
          try {
            await axios.post(`${CRICKET_API_URL}/api/cricket/contests/join`, {
              userId: currentUserId,
              contestId: contestToJoin._id || contestToJoin.id,
              userTeamId: newTeam._id || newTeam.id,
              matchId: matchId
            }, { headers: { Authorization: `Bearer ${token}` } });

            // 🔥 NAYA: Alert ki jagah Success Modal
            setShowSuccessModal({ show: true, message: "Team Saved & Contest Joined!" });
          } catch (joinErr) {
            alert(joinErr.response?.data?.error || "Team saved, but contest join failed.");
          }
        } else {
          // 🔥 NAYA: Alert ki jagah Success Modal
          setShowSuccessModal({
            show: true,
            message: actualTeamId ? "Team Updated Successfully!" : "Team Created Successfully!"
          });
        }

        // NOTE: navigate(-2) maine yahan se hata diya hai, 
        // kyunki wo kaam ab Modal ka "OK" button karega!
      }
    } catch (error) {
      console.error("Save Error:", error);
      alert(error.response?.data?.error || "Failed to save/update team");
    } finally {
      setLoading(false);
    }
  };

  const roleOrder = { "WK": 1, "BAT": 2, "AR": 3, "BOWL": 4 };
  const sortedPlayers = [...playersList].sort((a, b) => {
    if (sortBy === "type") {
      if (roleOrder[a.role] !== roleOrder[b.role]) return roleOrder[a.role] - roleOrder[b.role];
      return b.credits - a.credits;
    }
    if (sortBy === "points") return getChooseCaptainPoints(b) - getChooseCaptainPoints(a);
    return 0;
  });

  if (initializing) return (
    <div className="d-flex justify-content-center align-items-center min-vh-100 bg-white">
      <div className="spinner-border" style={{ color: "#800000" }}></div>
    </div>
  );

  return (
    <div className="bg-light min-vh-100 pb-5" style={{ fontFamily: "Roboto, sans-serif" }}>
      {/* === HEADER (MAROON THEME) === */}
      <div className="sticky-top shadow-sm" style={{ zIndex: 1040 }}>
        <div style={{ background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)" }} className="text-white pt-3 pb-2">

          <div className="d-flex justify-content-between align-items-start px-2 mb-2">

            {/* 🔥 LEFT: Back Button + Create Team + Timer 🔥 */}
            <div className="d-flex align-items-start gap-2" style={{ flex: 1 }}>
              <i className="bi bi-arrow-left fs-4" onClick={handleBack} style={{ cursor: "pointer", lineHeight: "1" }}></i>
              <div className="d-flex flex-column">
                {/* 🔥 text-nowrap add kiya taaki ek line me rahe 🔥 */}
                <p className="mb-0 fw-bold text-nowrap" style={{ fontSize: "13px", lineHeight: "1.2" }}>Create Team</p>
                <small className="text-white-50 fw-bold text-nowrap" style={{ fontSize: "11px" }}>{timeLeft}</small>
              </div>
            </div>

            {/* 🔥 CENTER: Logos + Matchup + C/VC Text 🔥 */}
            <div className="text-center d-flex flex-column align-items-center justify-content-center px-1" style={{ flex: 1.5 }}>
              <div className="d-flex align-items-center justify-content-center gap-1 mb-1">
                <img src={matchData?.team1?.image || "https://cdn-icons-png.flaticon.com/512/3344/3344394.png"} alt="T1" className="rounded-circle bg-white" style={{ width: '18px', height: '18px', padding: "1px" }} />
                <span className="fw-bold text-white" style={{ fontSize: "13px" }}>{matchData?.team1?.code || "T1"}</span>
                <span className="text-white-50 mx-1" style={{ fontSize: "10px" }}>vs</span>
                <span className="fw-bold text-white" style={{ fontSize: "13px" }}>{matchData?.team2?.code || "T2"}</span>
                <img src={matchData?.team2?.image || "https://cdn-icons-png.flaticon.com/512/3344/3344394.png"} alt="T2" className="rounded-circle bg-white" style={{ width: '18px', height: '18px', padding: "1px" }} />
              </div>

            </div>

            {/* 🔥 RIGHT: PTS Button 🔥 */}
            <div className="d-flex justify-content-end align-items-start" style={{ flex: 1 }}>
              <div className="rounded-circle border border-white border-opacity-50 d-flex align-items-center justify-content-center"
                style={{ width: '32px', height: '32px', fontSize: '10px', fontWeight: 'bold', cursor: 'pointer', backgroundColor: "rgba(255,255,255,0.1)" }}
                onClick={() => navigate('/point-system')}>
                PTS
              </div>
            </div>

          </div>

          <div className="fw-bold text-white opacity-75 text-center" style={{ fontSize: "13px" }}>Select Captain & Vice Captain</div>
        </div>

        {/* === INFO BANNER === */}
        <div className="bg-white px-3 py-2 border-bottom d-flex justify-content-center gap-4">
          <div className="d-flex align-items-center gap-2">
            <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-muted border border-secondary"
              style={{ width: "26px", height: "26px", fontSize: "12px", backgroundColor: "#f8f9fa" }}>C</div>
            <div className="d-flex flex-column">
              <span className="text-muted" style={{ fontSize: "10px", lineHeight: "1" }}>Captain gets</span>
              <span className="fw-bold text-dark" style={{ fontSize: "12px" }}>2x (double) points</span>
            </div>
          </div>

          <div className="border-start border-secondary opacity-25"></div>

          <div className="d-flex align-items-center gap-2">
            <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-muted border border-secondary"
              style={{ width: "26px", height: "26px", fontSize: "12px", backgroundColor: "#f8f9fa" }}>VC</div>
            <div className="d-flex flex-column">
              <span className="text-muted" style={{ fontSize: "10px", lineHeight: "1" }}>Vice Captain gets</span>
              <span className="fw-bold text-dark" style={{ fontSize: "12px" }}>1.5x points</span>
            </div>
          </div>
        </div>

        {/* === FILTER CHIPS === */}
        <div className="d-flex gap-2 px-3 py-2 bg-light border-bottom overflow-auto" style={{ whiteSpace: "nowrap", scrollbarWidth: "none" }}>
          <button className={`btn btn-sm rounded-pill px-3 ${sortBy === 'type' ? 'bg-white fw-bold shadow-sm border border-dark text-dark' : 'border text-muted bg-white'}`}
            onClick={() => setSortBy("type")}>
            Type {sortBy === 'type' ? '↓' : ''}
          </button>
          <button className={`btn btn-sm rounded-pill px-3 ${sortBy === 'points' ? 'bg-white fw-bold shadow-sm border border-dark text-dark' : 'border text-muted bg-white'}`}
            onClick={() => setSortBy("points")}>
            Points {sortBy === 'points' ? '↓' : ''}
          </button>
          <button className="btn btn-sm rounded-pill px-3 border text-muted bg-white">
            % Captain By
          </button>
          <button className="btn btn-sm rounded-pill px-3 border text-muted bg-white">
            % Vice Captain By
          </button>
        </div>

        {/* === LIST HEADER === */}
        <div className="d-flex px-3 py-2 bg-white text-muted border-bottom align-items-center fw-bold text-uppercase" style={{ fontSize: '10px' }}>
          <div style={{ width: "60px" }}>Type</div>
          <div className="flex-grow-1">Player</div>
          <div className="text-center" style={{ width: "55px" }}>Points</div>
          <div className="text-center" style={{ width: "55px" }}>% C</div>
          <div className="text-center" style={{ width: "55px" }}>% VC</div>
        </div>
      </div>

      {/* === PLAYER LIST (FIXED UI) === */}
      {selectionWarning && (
        <div className="position-fixed w-100 d-flex justify-content-center" style={{ top: "205px", zIndex: 1050 }}>
          <div className="text-white text-center py-2 px-4 rounded-pill fw-bold shadow-lg" style={{ fontSize: "12px", backgroundColor: "#dc3545" }}>
            <i className="bi bi-exclamation-triangle-fill me-2"></i>{selectionWarning}
          </div>
        </div>
      )}

      <div className="bg-white" style={{ paddingBottom: "100px" }}>
        {sortedPlayers.map((player) => {
          const playerId = getPlayerId(player);
          const isUnavailable = !isPlayerAvailableForLeadership(player);
          const totalPoints = formatCaptainStat(getChooseCaptainPoints(player));
          const captainPercentage = Math.round(getChooseCaptainPercentage(player));
          const viceCaptainPercentage = Math.round(getChooseViceCaptainPercentage(player));

          return (
          <div
            key={playerId}
            className={`d-flex align-items-center px-3 py-2 border-bottom transition-all ${captainId === playerId ? 'bg-danger bg-opacity-10' :
                viceCaptainId === playerId ? 'bg-primary bg-opacity-10' : ''
              }`}
          >

            {/* PLAYER IMAGE & ROLE BADGE */}
            <div className="position-relative me-3 mt-1" style={{ width: '55px', height: '55px', flexShrink: 0 }}>
              <i className="bi bi-info-circle-fill position-absolute bg-white rounded-circle text-secondary"
                style={{ top: '0', left: '-5px', fontSize: '12px', zIndex: 2 }}></i>

              <img
                src={player.image || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                alt={player.name}
                className="rounded-circle border"
                style={{ width: '100%', height: '100%', objectFit: 'cover', backgroundColor: "#f8f9fa" }}
                onError={(e) => { e.target.onerror = null; e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png"; }}
              />

              {/* Team & Role Badge (Bottom Centered) */}
              <div
                className="position-absolute d-flex shadow-sm bg-white"
                style={{
                  bottom: '-6px', left: '50%', transform: 'translateX(-50%)',
                  fontSize: '8px', borderRadius: "4px", overflow: "hidden",
                  whiteSpace: "nowrap", border: "1px solid #e5e7eb"
                }}
              >
                <span className="bg-light text-dark fw-bold px-1 border-end">{player.team || "T1"}</span>
                <span className="text-muted fw-bold px-1">{player.role || "PLYR"}</span>
              </div>
            </div>

            {/* PLAYER INFO */}
            <div className="flex-grow-1 ps-1 overflow-hidden">
              <h6 className="fw-bold mb-0 text-dark text-truncate" style={{ fontSize: "14px", paddingRight: "5px", maxWidth: "100%" }}>
                {player.name}
              </h6>
              {getPlayerStatusItems(player).map((status) => (
                <span key={status.key} className="d-inline-flex align-items-center gap-1 mt-1" style={{ fontSize: "10px", lineHeight: 1 }}>
                  <span className="rounded-circle flex-shrink-0" style={{ width: "7px", height: "7px", backgroundColor: status.color }}></span>
                  <span className="fw-semibold" style={{ color: status.color }}>{status.label}</span>
                </span>
              ))}
            </div>

            <div className="text-center text-muted fw-bold" style={{ width: "55px", fontSize: "12px", flexShrink: 0 }}>
              {totalPoints}
            </div>

            {/* C BUTTON */}
            <div className="text-center d-flex flex-column align-items-center" style={{ width: "55px", flexShrink: 0 }}>
              <button
                className={`btn btn-sm rounded-circle fw-bold shadow-sm d-flex align-items-center justify-content-center transition-all 
                  ${captainId === playerId ? "text-white border-0" : "bg-white text-secondary border"}`}
                onClick={() => toggleCaptain(player)}
                style={{
                  width: "38px", height: "38px", padding: 0, fontSize: "15px",
                  backgroundColor: captainId === playerId ? "#800000" : "transparent",
                  opacity: viceCaptainId === playerId || isUnavailable ? 0.4 : 1
                }}
                disabled={viceCaptainId === playerId || isUnavailable}
              >C</button>
              <small className="text-muted mt-1 fw-bold" style={{ fontSize: "9px" }}>{captainPercentage}%</small>
            </div>

            {/* VC BUTTON */}
            <div className="text-center d-flex flex-column align-items-center" style={{ width: "55px", flexShrink: 0 }}>
              <button
                className={`btn btn-sm rounded-circle fw-bold shadow-sm d-flex align-items-center justify-content-center transition-all 
                  ${viceCaptainId === playerId ? "text-white border-0" : "bg-white text-secondary border"}`}
                onClick={() => toggleViceCaptain(player)}
                style={{
                  width: "38px", height: "38px", padding: 0, fontSize: "15px",
                  backgroundColor: viceCaptainId === playerId ? "#0d6efd" : "transparent",
                  opacity: captainId === playerId || isUnavailable ? 0.4 : 1
                }}
                disabled={captainId === playerId || isUnavailable}
              >VC</button>
              <small className="text-muted mt-1 fw-bold" style={{ fontSize: "9px" }}>{viceCaptainPercentage}%</small>
            </div>

          </div>
        )})}
      </div>

      {/* === BOTTOM ACTION BAR === */}
      <div className="fixed-bottom bg-white p-3 shadow-lg d-flex gap-3 align-items-center justify-content-center" style={{ zIndex: 1040, borderTop: "1px solid #e5e7eb" }}>

        <button className="btn fw-bold text-dark d-flex align-items-center justify-content-center gap-2 bg-white"
          style={{ flex: 1, border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px", padding: "12px 0", letterSpacing: "0.5px" }}
          onClick={() => navigate("/team-preview", { state: { team: { players: playersList, captainId, viceCaptainId }, matchData } })}>
          <i className="bi bi-eye-fill fs-6"></i> PREVIEW
        </button>

        <button className={`btn fw-bold ${isComplete ? 'text-white' : 'text-muted'}`}
          style={{ flex: 1, background: isComplete ? "linear-gradient(135deg, #198754 0%, #146c43 100%)" : "#e5e7eb", border: "none", borderRadius: "8px", fontSize: "14px", padding: "12px 0", letterSpacing: "0.5px" }}
          disabled={!isComplete || loading}
          onClick={handleSaveTeam}>
          {loading ? <span className="spinner-border spinner-border-sm"></span> : "SAVE"}
        </button>
      </div>

      {/* === SUCCESS MODAL (PREMIUM WIN11 THEME) === */}
      {showSuccessModal.show && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center animate-fade-in"
          style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1060, backdropFilter: "blur(4px)" }}>
          <div className="card border-0 shadow-lg p-4 bg-white animate-slide-up"
            style={{ maxWidth: '340px', width: '90%', borderRadius: "16px", overflow: "hidden", position: "relative" }}>

            {/* Top Color Accent */}
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "6px", background: "linear-gradient(90deg, #800000, #dc3545)" }}></div>

            <div className="text-center mt-2">
              <div className="mx-auto mb-4 position-relative" style={{
                width: '72px', height: '72px', backgroundColor: '#fdf2f2', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: "0 4px 15px rgba(128, 0, 0, 0.1)"
              }}>
                <i className="bi bi-check-circle-fill" style={{ color: "#800000", fontSize: "38px" }}></i>
                {/* Subtle outer glow ring */}
                <div className="position-absolute w-100 h-100 rounded-circle border" style={{ borderColor: "#800000", opacity: 0.2, transform: "scale(1.2)" }}></div>
              </div>

              <h5 className="fw-bold mb-2" style={{ color: "#1e293b", fontSize: "20px" }}>Success!</h5>
              <p className="text-muted mb-4 fw-medium" style={{ fontSize: "14px", padding: "0 10px" }}>{showSuccessModal.message}</p>
            </div>

            <button className="btn w-100 fw-bold text-white shadow-sm"
              style={{
                background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)",
                borderRadius: "8px",
                padding: "12px 0",
                fontSize: "15px",
                letterSpacing: "1px",
                border: "none"
              }}
              onClick={() => {
                setShowSuccessModal({ show: false, message: "" });
                navigate(-2);
              }}>
              AWESOME
            </button>
          </div>

          {/* Simple CSS for animations */}
          <style>{`
            .animate-fade-in { animation: fadeIn 0.3s ease-out; }
            .animate-slide-up { animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes slideUp { from { transform: translateY(30px) scale(0.95); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
          `}</style>
        </div>
      )}

      <style>{`
        .transition-all { transition: all 0.2s ease-in-out; }
      `}</style>
    </div>
  );
}

export default ChooseCaptain;
